import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import FeaturesHero from "../components/home/FeaturesHero";
import FeaturesGrid from "../components/home/FeaturesGrid";
import FeaturesWorkflow from "../components/home/FeaturesWorkflow";
import SeoHead from "../components/seo/SeoHead";
import StructuredData, {
  buildOrganizationSchema,
  buildSoftwareApplicationSchema,
} from "../components/seo/StructuredData";
import MarketingPageShell from "../components/layout/MarketingPageShell";
import useThemeMode from "../hooks/useThemeMode";
import useI18n from "../i18n/useI18n";

function FeaturesPage() {
  const { isDark } = useThemeMode();
  const { t } = useI18n();

  const tt = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const featuredGuides = useMemo(
    () => [
      {
        title: tt("features.guides.whatIsHookora.title", "What Is Hookora?"),
        description: tt(
          "features.guides.whatIsHookora.description",
          "Learn what Hookora is, how it works, and why it helps creators move from idea to stronger content faster across a full 22-tool Studio workflow."
        ),
        to: "/blog/what-is-hookora",
      },
      {
        title: tt(
          "features.guides.nigerianCreators.title",
          "Hookora for Nigerian Creators"
        ),
        description: tt(
          "features.guides.nigerianCreators.description",
          "See how Hookora helps Nigerian creators build stronger hooks, titles, captions, scripts, localized rewrites, and better content packaging."
        ),
        to: "/blog/hookora-for-nigerian-creators",
      },
      {
        title: tt(
          "features.guides.writeHooksFaster.title",
          "How Hookora Helps Creators Write Hooks Faster"
        ),
        description: tt(
          "features.guides.writeHooksFaster.description",
          "Explore how Hookora helps creators write hooks faster with clearer angles, stronger first lines, better retention support, and more flexible creator workflow tools."
        ),
        to: "/blog/how-hookora-helps-creators-write-hooks-faster",
      },
    ],
    [t]
  );

  const planFeatureGroups = useMemo(
    () => [
      {
        title: tt("features.planGroups.free.title", "Free plan"),
        subtitle: tt(
          "features.planGroups.free.subtitle",
          "Start with the core writing workflow"
        ),
        description: tt(
          "features.planGroups.free.description",
          "Free users can access Hookora’s essential Studio tools for everyday content writing and idea shaping."
        ),
        features: [
          tt("tools.hooks", "Hooks"),
          tt("tools.titles", "Titles"),
          tt("tools.captions", "Captions"),
          tt("tools.hashtags", "Hashtags"),
          tt("tools.descriptions", "Descriptions"),
          tt("tools.scripts", "Scripts"),
        ],
      },
      {
        title: tt("features.planGroups.creator.title", "Creator plan"),
        subtitle: tt(
          "features.planGroups.creator.subtitle",
          "Unlock premium workflow tools"
        ),
        description: tt(
          "features.planGroups.creator.description",
          "Creator unlocks the premium middle layer of Hookora for packaging, rewrites, retention, repurposing, and stronger content execution."
        ),
        features: [
          tt("tools.hookAngles", "Hook Angles"),
          tt("tools.ctas", "CTAs"),
          tt("tools.thumbnailAnalyzer", "Thumbnail Analyzer"),
          tt("tools.aiHumanizer", "AI Humanizer"),
          tt("tools.repurposer", "Repurposer"),
          tt("tools.adCopy", "Ad Copy"),
          tt("tools.carousels", "Carousels"),
          tt("tools.viralRewrite", "Viral Rewrite"),
          tt("tools.offerPositioning", "Offer Positioning"),
          tt("tools.hookScore", "Hook Score"),
          tt("tools.nigerianRewrite", "Nigerian Rewrite"),
          tt("tools.retentionIntro", "Retention Intro"),
          tt("tools.commentIdeas", "Comment Ideas"),
        ],
      },
      {
        title: tt("features.planGroups.pro.title", "Pro plan"),
        subtitle: tt(
          "features.planGroups.pro.subtitle",
          "Unlock advanced strategic tools"
        ),
        description: tt(
          "features.planGroups.pro.description",
          "Pro is built for deeper strategic creator work, helping users turn stronger thinking into stronger content systems."
        ),
        features: [
          tt("tools.trendConverter", "Trend Converter"),
          tt("tools.brandVoiceTrainer", "Brand Voice Trainer"),
          tt("tools.contentDifferentiation", "Content Differentiation"),
        ],
      },
    ],
    [t]
  );

  const colors = {
    sectionBg: isDark ? "#020617" : "#f8fafc",
    cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
    cardSoftBg: isDark ? "rgba(30,41,59,0.72)" : "#ffffff",
    border: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
    borderStrong: isDark ? "rgba(148,163,184,0.28)" : "#d1d5db",
    text: isDark ? "#f8fafc" : "#111827",
    textSoft: isDark ? "#cbd5e1" : "#374151",
    textMuted: isDark ? "#94a3b8" : "#6b7280",
    badgeBg: isDark ? "rgba(109,40,217,0.18)" : "#f5f3ff",
    badgeText: isDark ? "#d8b4fe" : "#6d28d9",
    freeBg: isDark ? "rgba(120,53,15,0.14)" : "#fff7ed",
    freeBorder: isDark ? "rgba(251,191,36,0.22)" : "#fdba74",
    freeText: isDark ? "#fde68a" : "#9a3412",
    creatorBg: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
    creatorBorder: isDark ? "rgba(96,165,250,0.22)" : "#bfdbfe",
    creatorText: isDark ? "#bfdbfe" : "#1d4ed8",
    proBg: isDark ? "rgba(109,40,217,0.14)" : "#f5f3ff",
    proBorder: isDark ? "rgba(216,180,254,0.22)" : "#d8b4fe",
    proText: isDark ? "#e9d5ff" : "#7c3aed",
    shadow: isDark
      ? "0 14px 40px rgba(2, 6, 23, 0.45)"
      : "0 12px 30px rgba(15, 23, 42, 0.06)",
  };

  const getPlanCardStyle = (title) => {
    if (title === tt("features.planGroups.creator.title", "Creator plan")) {
      return {
        background: colors.creatorBg,
        border: colors.creatorBorder,
        color: colors.creatorText,
      };
    }

    if (title === tt("features.planGroups.pro.title", "Pro plan")) {
      return {
        background: colors.proBg,
        border: colors.proBorder,
        color: colors.proText,
      };
    }

    return {
      background: colors.freeBg,
      border: colors.freeBorder,
      color: colors.freeText,
    };
  };

  return (
    <MarketingPageShell>
      <SeoHead
        title={tt(
          "features.seoTitle",
          "Hookora Features | 22 AI Tools for Hooks, Titles, Captions, Scripts and More"
        )}
        description={tt(
          "features.seoDescription",
          "Explore Hookora features for creators, including 22 Studio tools across hooks, hook angles, titles, captions, hashtags, descriptions, scripts, CTAs, thumbnail text, rewrites, retention tools, localization, trend conversion, brand voice, and more."
        )}
        path="/features"
      />

      <StructuredData id="hookora-org-schema" data={buildOrganizationSchema()} />
      <StructuredData
        id="hookora-features-software-schema"
        data={buildSoftwareApplicationSchema({
          description: tt(
            "features.softwareSchemaDescription",
            "Hookora is an AI workflow platform for creators with 22 Studio tools for hooks, hook angles, titles, captions, hashtags, descriptions, scripts, CTAs, thumbnail text, rewrites, localization, retention intros, trend conversion, brand voice training, differentiation, and more."
          ),
        })}
      />

      <FeaturesHero />
      <FeaturesGrid />
      <FeaturesWorkflow />

      <section style={{ padding: "8px 20px 56px", background: colors.sectionBg }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "24px" }}>
            <div
              style={{
                display: "inline-flex",
                padding: "8px 14px",
                borderRadius: "999px",
                background: colors.badgeBg,
                color: colors.badgeText,
                fontWeight: "700",
                fontSize: "12px",
                marginBottom: "14px",
                border: isDark ? `1px solid ${colors.border}` : "none",
              }}
            >
              {tt("features.planBasedStudioAccess", "Plan-based Studio access")}
            </div>

            <h2
              style={{
                margin: "0 0 10px",
                color: colors.text,
                fontSize: "clamp(1.9rem, 4vw, 2.8rem)",
                lineHeight: "1.1",
              }}
            >
              {tt("features.planSectionTitle", "22 Studio tools across Free, Creator, and Pro")}
            </h2>

            <p
              style={{
                margin: 0,
                color: colors.textMuted,
                fontSize: "16px",
                lineHeight: "1.75",
                maxWidth: "820px",
                marginInline: "auto",
              }}
            >
              {tt(
                "features.planSectionDescription",
                "Hookora is now structured to give users a clear upgrade path: core tools on Free, premium workflow tools on Creator, and advanced strategic tools on Pro."
              )}
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "18px",
            }}
          >
            {planFeatureGroups.map((group) => {
              const planStyle = getPlanCardStyle(group.title);

              return (
                <article
                  key={group.title}
                  style={{
                    background: colors.cardBg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "24px",
                    padding: "24px",
                    boxShadow: colors.shadow,
                    backdropFilter: isDark ? "blur(14px)" : "none",
                  }}
                >
                  <div
                    style={{
                      display: "inline-flex",
                      padding: "7px 12px",
                      borderRadius: "999px",
                      background: planStyle.background,
                      border: `1px solid ${planStyle.border}`,
                      color: planStyle.color,
                      fontWeight: "800",
                      fontSize: "12px",
                      marginBottom: "14px",
                    }}
                  >
                    {group.title}
                  </div>

                  <h3
                    style={{
                      margin: "0 0 8px",
                      color: colors.text,
                      fontSize: "24px",
                      lineHeight: "1.25",
                    }}
                  >
                    {group.subtitle}
                  </h3>

                  <p
                    style={{
                      margin: "0 0 18px",
                      color: colors.textSoft,
                      fontSize: "15px",
                      lineHeight: "1.75",
                    }}
                  >
                    {group.description}
                  </p>

                  <div
                    style={{
                      display: "grid",
                      gap: "10px",
                    }}
                  >
                    {group.features.map((feature) => (
                      <div
                        key={feature}
                        style={{
                          background: colors.cardSoftBg,
                          border: `1px solid ${colors.borderStrong}`,
                          borderRadius: "14px",
                          padding: "10px 12px",
                          color: colors.text,
                          fontWeight: "700",
                          fontSize: "14px",
                          lineHeight: "1.55",
                        }}
                      >
                        {feature}
                      </div>
                    ))}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section style={{ padding: "8px 20px 56px", background: colors.sectionBg }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "24px" }}>
            <div
              style={{
                display: "inline-flex",
                padding: "8px 14px",
                borderRadius: "999px",
                background: colors.badgeBg,
                color: colors.badgeText,
                fontWeight: "700",
                fontSize: "12px",
                marginBottom: "14px",
                border: isDark ? `1px solid ${colors.border}` : "none",
              }}
            >
              {tt("home.featuredGuidesBadge", "Featured Hookora Guides")}
            </div>

            <h2
              style={{
                margin: "0 0 10px",
                color: colors.text,
                fontSize: "clamp(1.9rem, 4vw, 2.8rem)",
                lineHeight: "1.1",
              }}
            >
              {tt("features.guidesSectionTitle", "Learn more about how Hookora works")}
            </h2>

            <p
              style={{
                margin: 0,
                color: colors.textMuted,
                fontSize: "16px",
                lineHeight: "1.75",
                maxWidth: "760px",
                marginInline: "auto",
              }}
            >
              {tt(
                "features.guidesSectionDescription",
                "Explore quick guides about what Hookora is, who it helps, and how it improves hook writing, content ideation, rewrites, packaging, and creator workflow."
              )}
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "18px",
            }}
          >
            {featuredGuides.map((guide) => (
              <article
                key={guide.to}
                style={{
                  background: colors.cardBg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "24px",
                  padding: "24px",
                  boxShadow: colors.shadow,
                  backdropFilter: isDark ? "blur(14px)" : "none",
                }}
              >
                <h3
                  style={{
                    margin: "0 0 12px",
                    color: colors.text,
                    fontSize: "24px",
                    lineHeight: "1.25",
                  }}
                >
                  {guide.title}
                </h3>

                <p
                  style={{
                    margin: "0 0 18px",
                    color: colors.textSoft,
                    fontSize: "15px",
                    lineHeight: "1.75",
                  }}
                >
                  {guide.description}
                </p>

                <Link
                  to={guide.to}
                  style={{
                    display: "inline-flex",
                    textDecoration: "none",
                    background: colors.cardSoftBg,
                    color: colors.text,
                    padding: "12px 16px",
                    borderRadius: "12px",
                    fontWeight: "800",
                    border: `1px solid ${colors.borderStrong}`,
                  }}
                >
                  {tt("common.readGuide", "Read Guide")}
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>
    </MarketingPageShell>
  );
}

export default FeaturesPage;